import { detailedDiff } from "deep-object-diff";
import { FormikErrors } from "formik";
import { has, isEmpty, isUndefined, omitBy } from "lodash";
import {
  AchievementAggregation,
  AchievementBEFormat,
  AchievementCompleteRulePeriod,
  AchievementCompleteRulePeriodUnion,
  AchievementFEFormat,
  AchievementRuleTypes,
  AchievementTriggers,
  AchievementTriggersUnion,
  BEAchievementLimit,
  FEAvailableAchievementAggregationType,
  FEAvailableAchievementType,
} from "types/achievements";
import { CampaignCondition, SelectedLimitType } from "types/campaigns";
import {
  AchievementStepsIds,
  AchievementStepsUnion,
  Status,
  StepDetails,
} from "types/stepper";
import { parseCampaignTranslationsArrayError } from "utilities/backendErrors";
import {
  customerConditions,
  transactionConditions,
} from "../Campaign/Common/CampaignRules/rules";
import {
  findConditionIdPath,
  prepareFECampaignLimitFormatForBE,
  referralCustomerConditions,
  transformTransactionAttribute,
  transformTranslations,
} from "../Campaign/Common/campaignHelpers";
import { getAchievementInfoScheme, getAchievementRulesScheme } from "./schemas";

// Helper function to transform customer.tiers to customer.tiersIds in conditions
const transformCustomerTiersToTiersIds = (conditions: CampaignCondition[]) => {
  return conditions.map((condition) => {
    if (condition.attribute === "customer.tiers") {
      return {
        ...condition,
        attribute: "customer.tiersIds",
      };
    }
    if (condition.attribute === "referrer.tiers") {
      return {
        ...condition,
        attribute: "referrer.tiersIds",
      };
    }
    return condition;
  });
};

export const transactionDirectAchievementConditions: CampaignCondition[] = [
  ...transformCustomerTiersToTiersIds(customerConditions).map((obj) => ({
    ...obj,
    category: "memberDetails",
  })),
  ...(transactionConditions
    .filter(
      (item) => !findConditionIdPath(item)?.includes("transaction.items."),
    )
    .map((obj) => ({
      ...obj,
      category: "transaction",
    })) as CampaignCondition[]),
  {
    attribute: "expression",
    label: "expression",
    operator: "expression",
    type: "expression",
  },
];

export const transactionReferralAchievementConditions: CampaignCondition[] = [
  ...transformCustomerTiersToTiersIds(referralCustomerConditions).map(
    (obj) => ({
      ...obj,
      category: "memberDetails",
    }),
  ),
  ...(transactionConditions
    .filter(
      (item) => !findConditionIdPath(item)?.includes("transaction.items."),
    )
    .map((obj) => ({
      ...obj,
      category: "transaction",
    })) as CampaignCondition[]),
  {
    attribute: "expression",
    label: "expression",
    operator: "expression",
    type: "expression",
  },
];

export const customEventDirectAchievementConditions: CampaignCondition[] = [
  ...transformCustomerTiersToTiersIds(customerConditions).map((obj) => ({
    ...obj,
    category: "memberDetails",
  })),
  {
    attribute: "event.eventDate",
    category: "customEvent",
    data: null,
    label: "eventDate",
    type: "datetime",
  },
  {
    attribute: "expression",
    category: "customEvent",
    label: "expression",
    operator: "expression",
    type: "expression",
  },
];

export const customEventReferralAchievementConditions: CampaignCondition[] = [
  ...transformCustomerTiersToTiersIds(referralCustomerConditions).map(
    (obj) => ({
      ...obj,
      category: "memberDetails",
    }),
  ),
  {
    attribute: "event.eventDate",
    category: "customEvent",
    data: null,
    label: "eventDate",
    type: "datetime",
  },
  {
    attribute: "expression",
    category: "customEvent",
    label: "expression",
    operator: "expression",
    type: "expression",
  },
];

export const aggregationTypes: FEAvailableAchievementAggregationType[] = [
  {
    aggregation: AchievementAggregation.QUANTITY,
    descriptionText: (trigger: AchievementTriggersUnion) =>
      trigger === AchievementTriggers.TRANSACTION
        ? "achievementEventOccurenciesTransaction"
        : "achievementEventOccurenciesCustomEvent",
    label: "numberOfEventOccurrences",
  },
  {
    aggregation: AchievementAggregation.EXPRESSION,
    descriptionText: (trigger: AchievementTriggersUnion) =>
      trigger === AchievementTriggers.TRANSACTION
        ? "achievementSpecificEventAttributesTransaction"
        : "achievementSpecificEventAttributesCustomEvent",
    label: "numberOfSpecificEventAttributes",
  },
];

export const achievementTypes: FEAvailableAchievementType[] = [
  {
    descriptionText: "directAchievementInfo",
    label: "directAchievement",
    type: AchievementRuleTypes.DIRECT,
  },
  {
    descriptionText: "referralAchievementInfo",
    label: "referralAchievement",
    type: AchievementRuleTypes.REFERRAL,
  },
];

export const prepareBEAchievementLimitFormatForFE = (
  limits: BEAchievementLimit,
): SelectedLimitType => {
  if (limits && !Object.keys(limits)?.length) {
    return { interval: { type: "noLimit" } };
  } else if (limits?.hasOwnProperty("interval")) {
    return limits as SelectedLimitType;
  } else return { ...limits, interval: { type: "inTotal" } };
};

type Translation = {
  [iso: string]: { name: string; description?: string };
};

export const markTranslationAsDuplicate = (
  translations: Translation,
  message: (id: { id: string }) => string,
) => {
  const duplicatedTranslations: Translation = {};

  Object.keys(translations).forEach((iso) => {
    duplicatedTranslations[iso] = {
      name: `${message({ id: "copy" }).toUpperCase()} -  ${
        translations[iso].name
      }`,
      description: translations[iso].description,
    };
  });

  return duplicatedTranslations;
};

export const formatSubmitValues = (
  achievement: AchievementFEFormat,
): AchievementBEFormat => {
  const { TRANSACTION, CUSTOM_EVENT } = AchievementTriggers;
  const { EXPRESSION, QUANTITY } = AchievementAggregation;
  const { OVERALL, LAST_DAY } = AchievementCompleteRulePeriod;
  const { REFERRAL } = AchievementRuleTypes;

  return {
    active: achievement.active,
    activity:
      achievement.activity?.operator === "always_available"
        ? { data: null, operator: "" }
        : achievement.activity,
    limit: prepareFECampaignLimitFormatForBE(achievement.limit),
    translations: transformTranslations(achievement.translations),
    rules: achievement.rules?.map((item, _) =>
      (({
        type,
        trigger,
        event,
        limit,
        conditions,
        aggregation,
        completeRule,
        uniqueReferee,
        translations,
        achievementRuleId,
      }) => {
        return {
          achievementRuleId,
          aggregation: {
            type: aggregation.type,
            rule:
              aggregation.type !== EXPRESSION ? undefined : aggregation.rule,
          },
          completeRule: {
            period: {
              consecutive:
                completeRule.period?.type === OVERALL ||
                completeRule.period?.type === LAST_DAY
                  ? undefined
                  : completeRule.period?.consecutive,
              // Replace FE streakType value with the type in BE request
              type:
                completeRule.period?.type ===
                  AchievementCompleteRulePeriod.STREAK &&
                completeRule.period.streakType
                  ? completeRule.period.streakType
                  : completeRule.period?.type,
              value:
                completeRule.period?.type === LAST_DAY
                  ? completeRule.period.value
                  : undefined,
            },
            periodGoal: completeRule.periodGoal,
            onlyUniqueAttributeValues: undefined,
            uniqueAttribute:
              trigger === CUSTOM_EVENT && completeRule.onlyUniqueAttributeValues
                ? completeRule.uniqueAttribute
                : undefined,
          },
          conditions: conditions?.map(
            ({ attribute, param, data, operator }) => {
              attribute = transformTransactionAttribute(attribute, param);

              const condition =
                attribute === "expression"
                  ? { data, operator }
                  : { data, operator, attribute };

              return condition;
            },
          ),
          event: trigger !== CUSTOM_EVENT ? undefined : event,
          limit: prepareFECampaignLimitFormatForBE(limit as SelectedLimitType),
          uniqueReferee:
            type !== REFERRAL ||
            trigger !== TRANSACTION ||
            aggregation.type !== QUANTITY
              ? undefined
              : uniqueReferee,
          trigger: trigger,
          type: type,
          translations: transformTranslations(translations),
        };
      })(item),
    ),
  };
};

export const parseRulesErrors = (
  errors: {
    [key: number]: {
      [key: string]: any;
    };
  },
  translations: { name?: string; locale?: string; description?: string }[],
) => {
  for (const key in errors) {
    return {
      [key]: {
        ...errors[key],
        translations: parseCampaignTranslationsArrayError(
          errors[key].translations,
          translations,
        ),
      },
    };
  }
};

const checkIfRuleFieldsThatDoNotResetProgressWereModified = (
  values: AchievementFEFormat,
  initialValues: AchievementFEFormat,
) => {
  const {
    added: addedRules,
    deleted: deletedRules,
    updated: updatedRules,
  } = detailedDiff(initialValues.rules, values.rules);

  const editedRulesFields = Object.values(updatedRules);
  const addedRulesFields = Object.values(addedRules);
  const deletedRulesFields = Object.values(deletedRules);
  const fieldsNotResettingProgress = ["translations", "conditions"];

  const isRuleRemoved =
    deletedRulesFields.length >= 1 &&
    // When rule is removed, the lib returns undefined.
    deletedRulesFields.some((field) => field === undefined);

  if (isRuleRemoved) {
    return false;
  }

  const allEditedFields = [
    ...editedRulesFields,
    ...addedRulesFields,
    // Still needed - when the condition is removed there is an object with info.
    ...deletedRulesFields,
  ]
    .map((el) => Object.keys(el))
    .flat();

  const onlyFieldsThatDidNotResetProgressWereEdited = allEditedFields.every(
    (field) => fieldsNotResettingProgress.includes(field),
  );

  return onlyFieldsThatDidNotResetProgressWereEdited;
};

export const checkIfFieldsThatResetProgressWereModified = (
  values: AchievementFEFormat,
  initialValues: AchievementFEFormat,
) => {
  const prepareValuesToCompare = (values: AchievementFEFormat) => ({
    ...values,
    rules: values.rules.map((rule) => {
      if (
        rule.aggregation.type === AchievementAggregation.EXPRESSION &&
        rule.aggregation?.rule
      ) {
        return {
          ...rule,
          aggregation: {
            type: rule.aggregation.type,
            rule: rule.aggregation.rule.replace(/\s/g, ""),
          },
        };
      } else {
        return rule;
      }
    }),
  });

  const modifiedAchievementFields = detailedDiff(
    prepareValuesToCompare(initialValues),
    prepareValuesToCompare(values),
  );

  const achievementRulesWereModified =
    has(modifiedAchievementFields.added, "rules") ||
    has(modifiedAchievementFields.deleted, "rules") ||
    has(modifiedAchievementFields.updated, "rules");

  if (achievementRulesWereModified) {
    const onlyRuleFieldsThatDoNotResetProgressWereModified =
      checkIfRuleFieldsThatDoNotResetProgressWereModified(
        values,
        initialValues,
      );

    return onlyRuleFieldsThatDoNotResetProgressWereModified ? false : true;
  } else {
    return false;
  }
};

export const formatPatchSumbitValues = (
  properties: AchievementBEFormat,
  code: string,
) => ({
  active: properties.active,
  limit: properties.limit,
  translations: properties.translations,
  activity: properties.activity,
  rules: properties.rules?.map((rule) => ({
    translations: {
      [code]: {
        name: (rule.translations && rule.translations[code].name) || "",
        description:
          (rule.translations && rule.translations[code].description) || "",
      },
    },
    conditions: rule.conditions,
    achievementRuleId: rule.achievementRuleId,
  })),
});

const { ACHIEVEMENT_INFO, RULES, SUMMARY } = AchievementStepsIds;
const { ACTIVE, NOT_VISITED } = Status;

export const achievementFormSteps: StepDetails<AchievementStepsUnion>[] = [
  {
    id: ACHIEVEMENT_INFO,
    label: "achievementInfo",
    description: "achievementInfoStepDescription",
    status: ACTIVE,
  },
  {
    id: RULES,
    label: "rules",
    description: "rulesStepDescription",
    status: NOT_VISITED,
  },
  {
    id: SUMMARY,
    label: "summary",
    description: "summaryStepDescription",
    status: NOT_VISITED,
  },
];

export const getValidationSchemaByStep = (
  activeStepId: AchievementStepsUnion,
  message: any,
) => {
  switch (activeStepId) {
    case ACHIEVEMENT_INFO:
      return getAchievementInfoScheme(message);
    case RULES:
      return getAchievementRulesScheme(message);
    default:
      return undefined;
  }
};

export const getAchievementInfoFields = (fields: AchievementFEFormat) => ({
  translations: [{ name: fields.translations[0].name }],
  activity: fields.activity,
  limit: fields.limit,
});

export const getRulesFields = (fields: AchievementFEFormat) => ({
  rules: fields.rules,
});

export const getFieldsForFeValidation = (
  activeStepId: AchievementStepsUnion,
  formValues: AchievementFEFormat,
): Partial<AchievementFEFormat> => {
  switch (activeStepId) {
    case ACHIEVEMENT_INFO:
      return getAchievementInfoFields(formValues);
    case RULES:
      return getRulesFields(formValues);
    default:
      return {};
  }
};

export const getStepIdToSetError = (
  errors: FormikErrors<AchievementFEFormat>,
  values: AchievementFEFormat,
) => {
  const filteredErrors = omitBy(
    errors,
    (val, _) => isUndefined(val) || isEmpty(val),
  );
  const allBeErrorsFields = Object.keys(filteredErrors);

  const achievementFields = Object.keys(getAchievementInfoFields(values));
  const rulesFields = Object.keys(getRulesFields(values));

  const stepIds = allBeErrorsFields
    .map((errorField) => {
      switch (true) {
        case achievementFields.includes(errorField):
          return ACHIEVEMENT_INFO;
        case rulesFields.includes(errorField):
          return RULES;
        default:
          return undefined;
      }
    })
    .filter((id) => id !== undefined);

  return stepIds;
};

export const parseToFEPeriod = (period: {
  consecutive?: number;
  type: AchievementCompleteRulePeriodUnion;
  value?: number;
}) => {
  const { DAY, WEEK, MONTH, YEAR, STREAK } = AchievementCompleteRulePeriod;

  const isConsecutivePeriodType =
    period?.type === DAY ||
    period?.type === WEEK ||
    period?.type === MONTH ||
    period?.type === YEAR;

  if (isConsecutivePeriodType) {
    return {
      ...period,
      type: STREAK,
      streakType: period?.type,
    };
  } else {
    return period;
  }
};
